<template>
  <div>
    <MarketingHero
      :pre-title="t('hero.preTitle')"
      title-keypath="hero.title"
      :highlighted-title="t('hero.highlight')"
      :subtitle="t('hero.subtitle')"
      no-padding-bottom
      :is-highlighted-title-block="locale === 'en'"
    >
      <template #cta>
        <div
          v-if="isDesktopOrTablet"
          class="hidden md:block shrink-0"
        >
          <AppDownloadQrCode class="shadow-lg" />

          <div class="flex justify-center items-center gap-2 text-sm mt-2">
            <TIcon
              :icon="['fal', 'mobile-phone']"
              class="text-secondary"
            />
            <span>{{ t('hero.scan') }}</span>
          </div>
        </div>

        <AppMobileAppDownloadButtons
          size="lg"
          class="max-md:justify-center max-md:mx-auto lg:content-center"
        />
      </template>

      <template #image>
        <TImage
          :src="appPreviewImage"
          :alt="t('hero.imageAlt')"
          class="max-lg:max-h-96 mx-auto"
        />
      </template>
    </MarketingHero>

    <section class="py-12 bg-primary-950 text-white">
      <TContainer size="xl">
        <div class="flex justify-between items-center flex-col md:flex-row gap-8">
          <div class="space-y-1">
            <div class="text-3xl font-bold tracking-tight">
              100K+
            </div>
            <div class="text-white/50">
              {{ t('appDownload') }}
            </div>
          </div>

          <div class="grid grid-cols-[auto_1fr] gap-4">
            <img
              :src="appleLogoImage"
              alt=""
              class="w-10 h-auto"
            >
            <div class="space-y-1">
              <div class="text-3xl font-bold tracking-tight flex gap-4 items-center">
                4.7 <MoleculeRatingStars
                  variant="custom"
                  class="text-[#FFD166]"
                />
              </div>
              <div class="text-white/50">
                {{ t('basedOn', { count: n(1400) }) }}
              </div>
            </div>
          </div>

          <div class="grid grid-cols-[auto_1fr] gap-4">
            <img
              :src="playStoreLogoImage"
              alt=""
              class="w-10 h-auto"
            >
            <div class="space-y-1">
              <div class="text-3xl font-bold tracking-tight flex gap-4 items-center">
                4.2 <MoleculeRatingStars
                  variant="custom"
                  class="text-[#FB743F]"
                />
              </div>
              <div class="text-white/50">
                {{ t('basedOn', { count: n(700) }) }}
              </div>
            </div>
          </div>
        </div>
      </TContainer>
    </section>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  i18n: {
    paths: {
      en: '/app-download',
      fr: '/telecharger-application',
    },
  },
})

const { locale, n, t } = useI18n()
const { isDesktopOrTablet } = useDevice()

useSeoHeadTags({
  title: computed(() => (t('metaTitle'))),
  description: computed(() => (t('metaDescription'))),
})

const { getStaticImage, presets } = useImage()
const appPreviewImage = computed(() => getStaticImage({
  path: `/app/download/app-preview_${locale.value}.webp`,
  preset: presets.NATIVE_MD,
}))

const appleLogoImage = computed(() => getStaticImage({
  path: '/app/download/logo-apple.svg',
}))

const playStoreLogoImage = computed(() => getStaticImage({
  path: '/app/download/logo-play-store.svg',
}))

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: appPreviewImage.value,
    },
  ],
})
</script>

<i18n lang="json">
{
  "en": {
    "metaTitle": "Download the RVezy App",
    "metaDescription": "Download our mobile app, and get access to features on the go.",
    "hero": {
      "preTitle": "For travelers and hosts",
      "title": "Download the {highlight}",
      "highlight": "RVezy app",
      "subtitle": "The top-rated RV rental in North America",
      "scan": "Scan to download",
      "imageAlt": "RVezy mobile app preview"
    },
    "appDownload": "App downloads",
    "basedOn": "Based on {count}+ reviews"
  },
  "fr": {
    "metaTitle": "Téléchargez l’application RVezy",
    "metaDescription": "Téléchargez notre application mobile et accédez aux fonctionnalités tout en vous déplaçant.",
    "hero": {
      "preTitle": "Pour les voyageurs et les hôtes",
      "title": "Téléchargez l'{highlight}",
      "highlight": "application RVezy",
      "subtitle": "La location de VR la mieux cotée en Amérique du Nord",
      "scan": "Scanner pour télécharger",
      "imageAlt": "Aperçu de l'application mobile RVezy"
    },
    "appDownload": "Téléchargements d'applications",
    "basedOn": "Basé sur {count}+ avis"
  }
}
</i18n>
